import React, { useState } from "react";
import { 
  ChevronDown, 
  ChevronRight,
  Home,
  Users,
  Package,
  BookOpen,
  FileText,
  Image,
  Settings,
  Plus,
  Edit,
  Trash,
  Download,
  Camera,
  List,
  Grid,
  CheckCircle,
  Lock,
  Languages,
  Moon,
  AlertTriangle,
  Fingerprint,
  BarChart3,
  FolderOpen,
  Building2,
  LogIn,
  LogOut,
  Filter,
  Table
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";

const sections = [
  {
    id: "dashboard",
    title: "1. Dashboard (Início)",
    icon: Home,
    content: (
      <div className="space-y-4">
        <p className="text-stone-600">
          O Dashboard é a página principal da aplicação. Proporciona acesso rápido a todas as secções.
        </p>
        <div className="bg-gradient-to-br from-[#009392]/10 to-[#009392]/5 rounded-xl p-6 border border-[#009392]/20">
          <h4 className="font-semibold text-stone-800 mb-4">Secções disponíveis:</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { name: "Clientes", desc: "Gestão de clientes", icon: Users },
              { name: "PPM", desc: "Catálogo de produtos", icon: Package },
              { name: "Catálogos", desc: "Entregas físicas", icon: BookOpen },
              { name: "Promoção", desc: "Gestão de gabinetes", icon: Building2 },
              { name: "Estatísticas", desc: "Análise de dados", icon: BarChart3 },
              { name: "Projetos", desc: "Projetos dos clientes", icon: FolderOpen },
              { name: "Incidencias", desc: "Painéis com defeitos", icon: AlertTriangle },
              { name: "Catálogo Gresart", desc: "Abrir catálogo PDF", icon: Download },
              { name: "Secção Privada", desc: "Área protegida", icon: Lock }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-lg p-4 shadow-sm border border-stone-200">
                <item.icon className="w-8 h-8 text-[#009392] mb-2" />
                <p className="font-medium text-stone-800">{item.name}</p>
                <p className="text-xs text-stone-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="font-semibold text-amber-800 mb-2">⚙️ Acesso Rápido:</h4>
          <p className="text-amber-700 text-sm">No canto superior direito encontra os ícones de <strong>Perfil</strong>, <strong>Configurações</strong> e <strong>Guia</strong>.</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
            <LogIn size={16} /> Sistema de Login / Logout
          </h4>
          <p className="text-blue-700 text-sm mb-2">A aplicação tem dois modos:</p>
          <ul className="text-blue-700 text-sm space-y-1 ml-4 list-disc">
            <li><strong>Modo Edição:</strong> Pode criar, editar e eliminar dados</li>
            <li><strong>Modo Leitura:</strong> Apenas visualização, todas as ações de modificação estão bloqueadas</li>
          </ul>
          <p className="text-blue-600 text-xs mt-2">Clique em "Sair" no perfil para ativar o modo leitura. Use "Entrar" no Dashboard para voltar ao modo edição.</p>
        </div>
      </div>
    )
  },
  {
    id: "clientes",
    title: "2. Gestão de Clientes",
    icon: Users,
    content: (
      <div className="space-y-6">
        <p className="text-stone-600">
          Aceda através do menu superior → <strong>"Clientes"</strong>
        </p>
        
        {/* Criar cliente */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100">
            <h4 className="font-semibold text-emerald-800 flex items-center gap-2">
              <Plus size={18} /> Criar um novo cliente
            </h4>
          </div>
          <div className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#009392] text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <div>
                <p className="text-stone-700">Clique no botão <code className="bg-stone-100 px-2 py-0.5 rounded text-sm">+ Novo Cliente</code></p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#009392] text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
              <div>
                <p className="text-stone-700 mb-2">Preencha os campos:</p>
                <div className="bg-stone-50 rounded-lg p-3 grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2"><Image size={14} className="text-stone-400" /> Logo</div>
                  <div className="flex items-center gap-2"><span className="text-red-500">*</span> Nome</div>
                  <div>Pessoa de contacto</div>
                  <div>Telefone</div>
                  <div>Email</div>
                  <div>Morada</div>
                  <div>Cidade</div>
                  <div>País</div>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-[#009392] text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
              <p className="text-stone-700">Clique em <code className="bg-[#009392] text-white px-2 py-0.5 rounded text-sm">Criar</code></p>
            </div>
          </div>
        </div>

        {/* Editar/Eliminar */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="bg-blue-50 px-4 py-3 border-b border-blue-100">
              <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                <Edit size={18} /> Editar cliente
              </h4>
            </div>
            <div className="p-4">
              <p className="text-stone-600 text-sm">Clique no ícone do <strong>lápis</strong> no cartão do cliente → Modificar → <strong>Atualizar</strong></p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="bg-red-50 px-4 py-3 border-b border-red-100">
              <h4 className="font-semibold text-red-800 flex items-center gap-2">
                <Trash size={18} /> Eliminar cliente
              </h4>
            </div>
            <div className="p-4">
              <p className="text-stone-600 text-sm">Clique no ícone do <strong>caixote</strong> → Confirmar eliminação</p>
              <p className="text-red-600 text-xs mt-2">⚠️ Serão eliminados todos os produtos atribuídos</p>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: "detalle-cliente",
    title: "3. Detalhe do Cliente",
    icon: FileText,
    content: (
      <div className="space-y-6">
        <p className="text-stone-600">
          Aceda clicando em <strong>"Ver Showroom →"</strong> no cartão do cliente.
        </p>

        {/* Separadores */}
        <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
          <h4 className="font-semibold text-stone-800 mb-3">Separadores disponíveis:</h4>
          <div className="flex flex-wrap gap-2">
            {["Painéis Técnicos", "Expositores", "Showroom", "Ambientes", "Projetos", "Alterações", "Catálogos", "Notas"].map((tab, i) => (
              <span key={i} className="px-3 py-1.5 bg-white border border-stone-200 rounded-md text-sm text-stone-700">{tab}</span>
            ))}
          </div>
        </div>

        {/* Painéis Técnicos */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-[#009392]/10 px-4 py-3 border-b border-[#009392]/20">
            <h4 className="font-semibold text-[#009392]">3.1 Painéis Técnicos</h4>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <h5 className="font-medium text-stone-800 mb-2">Configurar tamanhos de painéis:</h5>
              <div className="flex flex-wrap gap-2 mb-2">
                {["1920x1005", "2400x1005", "2400x1200", "1200x1200", "2400x50"].map((size, i) => (
                  <span key={i} className="px-3 py-1 bg-stone-100 border border-stone-300 rounded text-sm">{size}</span>
                ))}
              </div>
              <p className="text-stone-500 text-sm">Selecione os tamanhos que o cliente utiliza (sem limite)</p>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <h5 className="font-medium text-amber-800 mb-1">💡 Aplicar tamanho a todos:</h5>
              <p className="text-amber-700 text-sm">Após selecionar os tamanhos, aparece a opção "Aplicar a todos:" para atribuir o mesmo tamanho a todos os produtos de uma vez.</p>
            </div>

            <div>
              <h5 className="font-medium text-stone-800 mb-2">Adicionar produtos:</h5>
              <ol className="list-decimal list-inside text-stone-600 text-sm space-y-1">
                <li>Clique em <code className="bg-stone-100 px-1 rounded">+ Adicionar Produtos</code></li>
                <li>Pesquise produtos por código ou coleção</li>
                <li>Selecione os produtos desejados</li>
                <li>Clique em <code className="bg-stone-100 px-1 rounded">Adicionar Selecionados</code></li>
              </ol>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <h5 className="font-medium text-yellow-800 mb-1 flex items-center gap-2">
                <AlertTriangle size={16} /> Notas de Produto:
              </h5>
              <p className="text-yellow-700 text-sm">Clique no ícone amarelo ⚠️ ao lado de cada produto para adicionar avisos (ex: "peça rota", "calcomania despegada").</p>
            </div>

            <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
              <h5 className="font-medium text-teal-800 mb-1 flex items-center gap-2">
                <BarChart3 size={16} /> Resumo de Painéis Técnicos:
              </h5>
              <p className="text-teal-700 text-sm mb-2">Cartões interativos que filtram os painéis exibidos:</p>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 bg-white border border-teal-300 rounded">Por medida (1920x1005, etc.)</span>
                <span className="px-2 py-1 bg-amber-100 border border-amber-300 rounded">Com Defeito</span>
                <span className="px-2 py-1 bg-stone-200 border border-stone-400 rounded">Descontinuados</span>
                <span className="px-2 py-1 bg-purple-100 border border-purple-300 rounded">Fora Catálogo</span>
                <span className="px-2 py-1 bg-red-100 border border-red-300 rounded">Sin Medida</span>
              </div>
              <p className="text-teal-600 text-xs mt-2">Clique num cartão para filtrar, clique em "Total" para ver todos.</p>
            </div>
          </div>
        </div>

        {/* Painéis Fora de Catálogo */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-teal-50 px-4 py-3 border-b border-teal-100">
            <h4 className="font-semibold text-teal-800">3.2 Painéis Fora de Catálogo</h4>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-stone-600 text-sm">Para painéis personalizados que não estão no catálogo PPM:</p>
            <ol className="list-decimal list-inside text-stone-600 text-sm space-y-1">
              <li>Na secção de Painéis Técnicos, clique em <code className="bg-stone-100 px-1 rounded">+ Adicionar Painel Fora de Catálogo</code></li>
              <li>Carregue uma imagem do painel</li>
              <li>Introduza um nome descritivo</li>
              <li>Clique em <code className="bg-stone-100 px-1 rounded">Adicionar</code></li>
            </ol>
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-2">
              <p className="text-teal-700 text-sm">✓ Estes painéis aparecem no relatório PDF com badge "FORA DE CATÁLOGO"</p>
            </div>
          </div>
        </div>

        {/* Total Investido */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100">
            <h4 className="font-semibold text-emerald-800">3.3 Total Investido</h4>
          </div>
          <div className="p-4">
            <p className="text-stone-600 text-sm mb-2">No topo da página do cliente aparece um resumo do investimento:</p>
            <div className="bg-gradient-to-r from-[#00A19A]/5 to-transparent border border-[#00A19A]/20 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-stone-500">Total Investido</p>
                  <p className="text-2xl font-bold text-[#00A19A]">€X.XXX,XX</p>
                </div>
                <div className="text-right text-sm">
                  <p className="text-stone-400">Painéis Técnicos: €XXX</p>
                  <p className="text-stone-400">Expositores: €XXX</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Expositores */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-purple-50 px-4 py-3 border-b border-purple-100">
            <h4 className="font-semibold text-purple-800">3.4 Expositores</h4>
          </div>
          <div className="p-4">
            <p className="text-stone-600 text-sm mb-2">Gestão de expositores <strong>sem configuração de tamanhos</strong>.</p>
            <p className="text-stone-500 text-sm">Processo igual aos Painéis Técnicos, mas mais simples.</p>
          </div>
        </div>

        {/* Showroom e Ambientes */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="bg-sky-50 px-4 py-3 border-b border-sky-100">
              <h4 className="font-semibold text-sky-800 flex items-center gap-2">
                <Camera size={18} /> 3.5 Showroom
              </h4>
            </div>
            <div className="p-4">
              <p className="text-stone-600 text-sm">Fotos do showroom do cliente.</p>
              <p className="text-stone-500 text-sm mt-2">Clique em <code className="bg-stone-100 px-1 rounded">+ Adicionar Fotos</code> → Selecionar imagens</p>
              <p className="text-stone-500 text-sm mt-1">💡 Clique na imagem para ampliar (lightbox)</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="bg-pink-50 px-4 py-3 border-b border-pink-100">
              <h4 className="font-semibold text-pink-800 flex items-center gap-2">
                <Image size={18} /> 3.6 Ambientes
              </h4>
            </div>
            <div className="p-4">
              <p className="text-stone-600 text-sm">Fotos de ambientes e instalações.</p>
              <p className="text-stone-500 text-sm mt-2">Mesmo processo que Showroom.</p>
            </div>
          </div>
        </div>

        {/* Alterações */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-orange-50 px-4 py-3 border-b border-orange-100">
            <h4 className="font-semibold text-orange-800">3.7 Alterações (Histórico de mudanças)</h4>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-stone-600 text-sm">Registo de entradas e saídas de produtos.</p>
            <div className="bg-stone-50 rounded-lg p-3">
              <h5 className="font-medium text-stone-700 mb-2">Registar uma alteração:</h5>
              <ol className="list-decimal list-inside text-stone-600 text-sm space-y-1">
                <li>Clique em <code className="bg-stone-100 px-1 rounded">+ Nova Alteração</code></li>
                <li>Selecione a data</li>
                <li>Marque produtos adicionados e/ou removidos</li>
                <li>Clique em <code className="bg-stone-100 px-1 rounded">Registar Alteração</code></li>
              </ol>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-emerald-700 text-sm">✓ As alterações atualizam automaticamente o stock do showroom</p>
            </div>
          </div>
        </div>

        {/* Catálogos (dentro do cliente) */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-100">
            <h4 className="font-semibold text-indigo-800">3.8 Catálogos (Entregas ao cliente)</h4>
          </div>
          <div className="p-4">
            <p className="text-stone-600 text-sm mb-2">Ver e editar catálogos entregues a este cliente.</p>
            <p className="text-stone-500 text-sm">Clique no ícone de lápis para editar a quantidade entregue.</p>
          </div>
        </div>

        {/* Notas */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-stone-100 px-4 py-3 border-b border-stone-200">
            <h4 className="font-semibold text-stone-800">3.9 Notas</h4>
          </div>
          <div className="p-4">
            <p className="text-stone-600 text-sm">Notas internas sobre o cliente. Escreva e guarde com <code className="bg-stone-100 px-1 rounded">Guardar Notas</code></p>
          </div>
        </div>

        {/* Projetos */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-100">
            <h4 className="font-semibold text-indigo-800 flex items-center gap-2">
              <FolderOpen size={18} /> 3.10 Projetos
            </h4>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-stone-600 text-sm">Organize ficheiros do cliente em pastas de projeto.</p>
            <div className="bg-stone-50 rounded-lg p-3">
              <h5 className="font-medium text-stone-700 mb-2">Criar um projeto:</h5>
              <ol className="list-decimal list-inside text-stone-600 text-sm space-y-1">
                <li>Clique em <code className="bg-stone-100 px-1 rounded">+ Novo Projeto</code></li>
                <li>Introduza o nome do projeto</li>
                <li>Clique em <code className="bg-stone-100 px-1 rounded">Criar</code></li>
              </ol>
            </div>
            <div className="bg-stone-50 rounded-lg p-3">
              <h5 className="font-medium text-stone-700 mb-2">Adicionar ficheiros:</h5>
              <ol className="list-decimal list-inside text-stone-600 text-sm space-y-1">
                <li>Selecione o projeto na lista</li>
                <li>Clique em <code className="bg-stone-100 px-1 rounded">+ Adicionar Ficheiros</code></li>
                <li>Selecione imagens ou PDFs do seu dispositivo</li>
              </ol>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-2">
              <p className="text-indigo-700 text-sm">✓ Os projetos também aparecem na página "Projetos" do Dashboard</p>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: "ppm",
    title: "4. PPM (Catálogo de Produtos)",
    icon: Package,
    content: (
      <div className="space-y-6">
        <p className="text-stone-600">
          Aceda através do menu superior → <strong>"PPM"</strong>
        </p>

        {/* Filtros */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-blue-50 px-4 py-3 border-b border-blue-100">
            <h4 className="font-semibold text-blue-800 flex items-center gap-2">
              <Filter size={18} /> Filtros disponíveis
            </h4>
          </div>
          <div className="p-4">
            <div className="flex flex-wrap gap-3">
              <div className="bg-stone-50 rounded-lg p-3 flex-1 min-w-[150px]">
                <p className="font-medium text-stone-700 text-sm mb-1">Tipologia</p>
                <p className="text-xs text-stone-500">Painéis Técnicos / Expositores</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-3 flex-1 min-w-[150px]">
                <p className="font-medium text-stone-700 text-sm mb-1">Series</p>
                <p className="text-xs text-stone-500">Filtrar por coleção</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-3 flex-1 min-w-[150px]">
                <p className="font-medium text-stone-700 text-sm mb-1">Estado</p>
                <p className="text-xs text-stone-500">Todos / Activos / Descontinuados</p>
              </div>
            </div>
            <p className="text-stone-500 text-xs mt-3">💡 O catálogo abre por defeito com "PAINÉIS TÉCNICOS" selecionado.</p>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100">
            <h4 className="font-semibold text-emerald-800 flex items-center gap-2">
              <Plus size={18} /> Criar produto
            </h4>
          </div>
          <div className="p-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h5 className="font-medium text-stone-700 mb-2">Campos do produto:</h5>
                <ul className="text-sm text-stone-600 space-y-1">
                  <li className="flex items-center gap-2"><Image size={14} /> Imagem</li>
                  <li className="flex items-center gap-2"><span className="text-red-500">*</span> Código (obrigatório)</li>
                  <li>Código Secundário</li>
                  <li>Coleção</li>
                  <li>Cor</li>
                  <li>Tamanho</li>
                  <li>Tipo de Produto</li>
                  <li>Valor (€)</li>
                  <li className="flex items-center gap-2"><span className="px-2 py-0.5 bg-stone-800 text-white text-xs rounded">Descontinuado</span></li>
                </ul>
              </div>
              <div className="bg-stone-50 rounded-lg p-4">
                <h5 className="font-medium text-stone-700 mb-2">Exemplo de produto:</h5>
                <div className="bg-white rounded border border-stone-200 p-3">
                  <div className="w-full h-20 bg-stone-100 rounded mb-2 flex items-center justify-center text-stone-400">
                    <Image size={24} />
                  </div>
                  <p className="font-mono text-xs text-[#009392]">B07GRPT0006</p>
                  <p className="text-xs text-stone-500">Coleção: STYLE</p>
                  <p className="text-xs text-stone-500">Cor: Branco</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ver clientes */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-violet-50 px-4 py-3 border-b border-violet-100">
            <h4 className="font-semibold text-violet-800 flex items-center gap-2">
              <Users size={18} /> Ver clientes com este produto
            </h4>
          </div>
          <div className="p-4">
            <p className="text-stone-600 text-sm mb-2">Clique no ícone 📍 em cada produto para ver quais clientes têm esse produto no showroom.</p>
            <p className="text-stone-500 text-xs">Clique no cliente para ir diretamente à sua página.</p>
          </div>
        </div>
      </div>
    )
  },
  {
    id: "catalogos",
    title: "5. Catálogos Físicos",
    icon: BookOpen,
    content: (
      <div className="space-y-6">
        <p className="text-stone-600">
          Aceda através do menu superior → <strong>"Catálogos"</strong>
        </p>

        {/* Tabs */}
        <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
          <h4 className="font-semibold text-stone-800 mb-3">Separadores:</h4>
          <div className="flex flex-wrap gap-2">
            <span className="px-3 py-1.5 bg-[#009392] text-white rounded-md text-sm">Clientes</span>
            <span className="px-3 py-1.5 bg-white border border-stone-200 rounded-md text-sm text-stone-700">Gabinetes</span>
          </div>
          <p className="text-stone-500 text-xs mt-2">Gerencie entregas tanto para Clientes como para Gabinetes (Promoção).</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-100">
              <h4 className="font-semibold text-indigo-800">Criar tipo de catálogo</h4>
            </div>
            <div className="p-4">
              <ol className="list-decimal list-inside text-stone-600 text-sm space-y-1">
                <li>Clique em <code className="bg-stone-100 px-1 rounded">+ Novo Catálogo</code></li>
                <li>Introduza o nome e ano</li>
                <li>Carregue uma imagem de capa (opcional)</li>
                <li>Clique em <code className="bg-stone-100 px-1 rounded">Criar</code></li>
              </ol>
              <div className="mt-3 bg-stone-50 rounded p-2">
                <p className="text-xs text-stone-500">Exemplos: CATALOGO GERAL 2026, STARDUST</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="bg-violet-50 px-4 py-3 border-b border-violet-100">
              <h4 className="font-semibold text-violet-800">Registar entrega</h4>
            </div>
            <div className="p-4">
              <ol className="list-decimal list-inside text-stone-600 text-sm space-y-1">
                <li>Clique em <code className="bg-stone-100 px-1 rounded">+ Nova Entrega</code></li>
                <li>Preencha: Cliente/Gabinete, Catálogo, Quantidade, Data</li>
                <li>Clique em <code className="bg-stone-100 px-1 rounded">Registar</code></li>
              </ol>
            </div>
          </div>
        </div>

        {/* Editar e Exportar */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="bg-blue-50 px-4 py-3 border-b border-blue-100">
              <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                <Edit size={18} /> Editar entregas
              </h4>
            </div>
            <div className="p-4">
              <p className="text-stone-600 text-sm">Expanda um cliente na lista para ver e editar as suas entregas.</p>
              <p className="text-stone-500 text-xs mt-2">Use o ícone 🖊️ para editar quantidade ou o 🗑️ para eliminar.</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100">
              <h4 className="font-semibold text-emerald-800 flex items-center gap-2">
                <Table size={18} /> Exportar dados
              </h4>
            </div>
            <div className="p-4">
              <p className="text-stone-600 text-sm mb-2">Exporte o relatório de entregas:</p>
              <div className="flex gap-2">
                <span className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">PDF</span>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">Excel</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="font-semibold text-amber-800 mb-2">🔍 Filtro por catálogo</h4>
          <p className="text-amber-700 text-sm">Use o filtro para ver entregas de um catálogo específico. Os totais serão recalculados para mostrar apenas esse catálogo.</p>
        </div>
      </div>
    )
  },
  {
    id: "pdf",
    title: "6. Geração de Relatórios PDF",
    icon: Download,
    content: (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-rose-50 px-4 py-3 border-b border-rose-100">
            <h4 className="font-semibold text-rose-800">Relatório de Cliente (Showroom)</h4>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <h5 className="font-medium text-stone-700 mb-2">Conteúdo do PDF:</h5>
              <ul className="grid grid-cols-2 gap-2 text-sm text-stone-600">
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-500" /> Logo da GRESART</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-500" /> Logo do cliente</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-500" /> Informação do cliente</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-500" /> Tamanhos de painéis</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-500" /> Painéis Técnicos (com tamanho)</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-500" /> Expositores (separados)</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-500" /> Notas de produtos ⚠️</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-500" /> Painéis Fora de Catálogo</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-500" /> Fotos do showroom</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-500" /> Fotos de ambientes</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-500" /> Total investido por categoria</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-500" /> Notas do cliente</li>
              </ul>
            </div>
            <div className="bg-stone-50 rounded-lg p-3">
              <h5 className="font-medium text-stone-700 mb-2">Como gerar:</h5>
              <ol className="list-decimal list-inside text-stone-600 text-sm space-y-1">
                <li>Ir a Clientes → Ver Showroom</li>
                <li>Clique em <code className="bg-[#009392] text-white px-2 py-0.5 rounded">Gerar Relatório</code></li>
                <li>Na pré-visualização, clique em <strong>"PDF"</strong> para descarregar</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: "tamanhos",
    title: "7. Tamanhos de Painéis",
    icon: Grid,
    content: (
      <div className="space-y-4">
        <p className="text-stone-600">Tamanhos de painéis disponíveis no sistema:</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { size: "1920x1005", w: "1920mm", h: "1005mm" },
            { size: "2400x1005", w: "2400mm", h: "1005mm" },
            { size: "2400x1200", w: "2400mm", h: "1200mm" },
            { size: "1200x1200", w: "1200mm", h: "1200mm" },
            { size: "2400x50", w: "2400mm", h: "50mm" }
          ].map((item, i) => (
            <div key={i} className="bg-white border-2 border-[#009392]/30 rounded-lg p-4 text-center">
              <p className="font-bold text-[#009392] text-lg">{item.size}</p>
              <p className="text-xs text-stone-500 mt-1">{item.w} × {item.h}</p>
            </div>
          ))}
        </div>
      </div>
    )
  },
  {
    id: "dicas",
    title: "8. Dicas e Boas Práticas",
    icon: Settings,
    content: (
      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          {[
            { title: "Imagens", tip: "Utilize imagens de boa qualidade mas otimizadas (máx 5MB)", color: "emerald" },
            { title: "Códigos", tip: "Mantenha um sistema consistente de códigos de produto", color: "blue" },
            { title: "Alterações", tip: "Registe sempre as entradas/saídas para manter o histórico", color: "amber" },
            { title: "Backups", tip: "Os dados são guardados automaticamente na nuvem", color: "violet" }
          ].map((item, i) => (
            <div key={i} className={`bg-${item.color}-50 border border-${item.color}-200 rounded-lg p-4`}>
              <h4 className={`font-semibold text-${item.color}-800 mb-1`}>{item.title}</h4>
              <p className={`text-${item.color}-700 text-sm`}>{item.tip}</p>
            </div>
          ))}
        </div>
      </div>
    )
  },
  {
    id: "configuracoes",
    title: "9. Configurações",
    icon: Settings,
    content: (
      <div className="space-y-6">
        <p className="text-stone-600">
          Aceda através do ícone ⚙️ no canto superior direito do Dashboard.
        </p>
        
        {/* Segurança */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-amber-50 px-4 py-3 border-b border-amber-100">
            <h4 className="font-semibold text-amber-800 flex items-center gap-2">
              <Lock size={18} /> Segurança (PIN)
            </h4>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-stone-600 text-sm">Proteja a Secção Privada com um PIN de 4 dígitos.</p>
            <ol className="list-decimal list-inside text-stone-600 text-sm space-y-1">
              <li>Clique em <code className="bg-stone-100 px-1 rounded">Definir PIN</code></li>
              <li>Introduza 4 dígitos (teclado numérico ou campo de texto)</li>
              <li>Confirme o PIN</li>
              <li>Clique em <code className="bg-stone-100 px-1 rounded">Guardar</code></li>
            </ol>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-3">
              <h5 className="font-medium text-purple-800 mb-1 flex items-center gap-2">
                <Fingerprint size={16} /> Face ID / Touch ID
              </h5>
              <p className="text-purple-700 text-sm">Após definir o PIN, pode ativar a autenticação biométrica (Face ID/Touch ID) como método adicional de desbloqueio.</p>
            </div>
          </div>
        </div>

        {/* Idioma */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-blue-50 px-4 py-3 border-b border-blue-100">
            <h4 className="font-semibold text-blue-800 flex items-center gap-2">
              <Languages size={18} /> Idioma
            </h4>
          </div>
          <div className="p-4">
            <p className="text-stone-600 text-sm mb-2">Idiomas disponíveis:</p>
            <div className="flex flex-wrap gap-2">
              {["🇵🇹 Português", "🇬🇧 English", "🇪🇸 Español", "🇫🇷 Français", "🇮🇹 Italiano"].map((lang, i) => (
                <span key={i} className="px-3 py-1 bg-stone-100 border border-stone-200 rounded text-sm">{lang}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Aparência */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-purple-50 px-4 py-3 border-b border-purple-100">
            <h4 className="font-semibold text-purple-800 flex items-center gap-2">
              <Moon size={18} /> Aparência
            </h4>
          </div>
          <div className="p-4">
            <p className="text-stone-600 text-sm">Alterne entre Modo Claro e Modo Escuro.</p>
          </div>
        </div>

        {/* Catálogo Gresart */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-red-50 px-4 py-3 border-b border-red-100">
            <h4 className="font-semibold text-red-800 flex items-center gap-2">
              <FileText size={18} /> Catálogo Gresart PDF
            </h4>
          </div>
          <div className="p-4">
            <p className="text-stone-600 text-sm mb-2">Carregue um catálogo PDF personalizado que será aberto ao clicar em "Catálogo Gresart" no Dashboard.</p>
            <p className="text-stone-500 text-sm">Suporta ficheiros grandes até 50MB (upload por partes).</p>
          </div>
        </div>

        {/* Dados */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-teal-50 px-4 py-3 border-b border-teal-100">
            <h4 className="font-semibold text-teal-800">Dados</h4>
          </div>
          <div className="p-4">
            <ul className="text-stone-600 text-sm space-y-2">
              <li><strong>Exportar Dados:</strong> Descarrega todos os dados em formato JSON</li>
              <li><strong>Limpar Cache:</strong> Limpa dados temporários da aplicação</li>
            </ul>
          </div>
        </div>
      </div>
    )
  },
  {
    id: "privada",
    title: "10. Secção Privada",
    icon: Lock,
    content: (
      <div className="space-y-6">
        <p className="text-stone-600">
          Área protegida para documentos confidenciais. Aceda através do botão "Secção Privada" no Dashboard.
        </p>
        
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="font-semibold text-amber-800 mb-2">🔐 Proteção por PIN</h4>
          <p className="text-amber-700 text-sm">Se tiver um PIN configurado, terá de o introduzir para aceder. Também pode usar Face ID/Touch ID se estiver ativo.</p>
        </div>

        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-stone-100 px-4 py-3 border-b border-stone-200">
            <h4 className="font-semibold text-stone-800">Separadores disponíveis:</h4>
          </div>
          <div className="p-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-stone-50 rounded-lg p-4 text-center">
                <FileText className="w-8 h-8 text-[#009392] mx-auto mb-2" />
                <p className="font-medium text-stone-800">Tabela de Preços</p>
                <p className="text-xs text-stone-500">PDFs de preços</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-4 text-center">
                <Download className="w-8 h-8 text-[#009392] mx-auto mb-2" />
                <p className="font-medium text-stone-800">Relatórios</p>
                <p className="text-xs text-stone-500">Relatórios internos</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-4 text-center">
                <Package className="w-8 h-8 text-[#009392] mx-auto mb-2" />
                <p className="font-medium text-stone-800">Vendas</p>
                <p className="text-xs text-stone-500">Documentos de vendas</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-stone-50 rounded-lg p-4">
          <h5 className="font-medium text-stone-700 mb-2">Adicionar documentos:</h5>
          <ol className="list-decimal list-inside text-stone-600 text-sm space-y-1">
            <li>Selecione o separador desejado</li>
            <li>Clique em <code className="bg-stone-100 px-1 rounded">+ Adicionar Documento</code></li>
            <li>Introduza um nome e selecione o ficheiro PDF</li>
            <li>Clique em <code className="bg-stone-100 px-1 rounded">Carregar</code></li>
          </ol>
        </div>
      </div>
    )
  },
  {
    id: "promocao",
    title: "11. Promoção (Gabinetes)",
    icon: Building2,
    content: (
      <div className="space-y-6">
        <p className="text-stone-600">
          Gestão de gabinetes de arquitectura, construtores e designers. Aceda através de <strong>"Promoção"</strong> no Dashboard.
        </p>
        
        {/* Tipos */}
        <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
          <h4 className="font-semibold text-stone-800 mb-3">Tipos de gabinete:</h4>
          <div className="flex flex-wrap gap-2">
            <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-md text-sm font-medium">ARQUITETOS</span>
            <span className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-md text-sm font-medium">CONSTRUTORES</span>
            <span className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-md text-sm font-medium">DESIGNERS</span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100">
            <h4 className="font-semibold text-emerald-800 flex items-center gap-2">
              <Plus size={18} /> Criar gabinete
            </h4>
          </div>
          <div className="p-4">
            <div className="bg-stone-50 rounded-lg p-3 grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2"><span className="text-red-500">*</span> Nome</div>
              <div>Tipo</div>
              <div>Morada</div>
              <div>Pessoa de contacto</div>
              <div>Email</div>
              <div>Telefone</div>
              <div className="flex items-center gap-2"><Image size={14} className="text-stone-400" /> Logo (opcional)</div>
            </div>
          </div>
        </div>

        {/* Detalhe do Gabinete */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-blue-50 px-4 py-3 border-b border-blue-100">
            <h4 className="font-semibold text-blue-800">Detalhe do Gabinete</h4>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-stone-600 text-sm">Ao clicar num gabinete, abre a página de detalhe com dois separadores:</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-stone-50 rounded-lg p-3">
                <h5 className="font-medium text-stone-700 mb-2">📅 Visitas</h5>
                <p className="text-stone-500 text-sm">Registe visitas com data e notas.</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-3">
                <h5 className="font-medium text-stone-700 mb-2">📚 Catálogos</h5>
                <p className="text-stone-500 text-sm">Registre entregas de catálogos físicos ao gabinete.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: "estatisticas",
    title: "12. Estatísticas",
    icon: BarChart3,
    content: (
      <div className="space-y-6">
        <p className="text-stone-600">
          Página de análise e resumo de dados. Aceda através de <strong>"Estatísticas"</strong> no Dashboard.
        </p>
        
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="bg-[#009392]/10 px-4 py-3 border-b border-[#009392]/20">
            <h4 className="font-semibold text-[#009392]">Métricas disponíveis</h4>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-stone-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-[#009392]">XX</p>
                <p className="text-xs text-stone-500">Clientes</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-[#009392]">XX</p>
                <p className="text-xs text-stone-500">Painéis</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-[#009392]">€XX</p>
                <p className="text-xs text-stone-500">Investimento</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-[#009392]">XX</p>
                <p className="text-xs text-stone-500">Catálogos</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="bg-amber-50 px-4 py-3 border-b border-amber-100">
              <h4 className="font-semibold text-amber-800">Top 10 Painéis</h4>
            </div>
            <div className="p-4">
              <p className="text-stone-600 text-sm">Os 10 painéis mais presentes nos showrooms dos clientes.</p>
              <p className="text-stone-500 text-xs mt-2">💡 Clique na imagem para ampliar com zoom.</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100">
              <h4 className="font-semibold text-emerald-800">Top Clientes</h4>
            </div>
            <div className="p-4">
              <p className="text-stone-600 text-sm">Clientes ordenados por investimento total.</p>
              <p className="text-stone-500 text-xs mt-2">Use "Ver todos" para expandir a lista completa.</p>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: "incidencias",
    title: "13. Incidencias",
    icon: AlertTriangle,
    content: (
      <div className="space-y-6">
        <p className="text-stone-600">
          Página para visualizar painéis com problemas. Aceda através de <strong>"Incidencias"</strong> no Dashboard.
        </p>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="bg-amber-50 px-4 py-3 border-b border-amber-100">
              <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                <AlertTriangle size={18} /> Painéis com Defeito
              </h4>
            </div>
            <div className="p-4">
              <p className="text-stone-600 text-sm mb-2">Lista de clientes que têm painéis com notas de defeito.</p>
              <p className="text-stone-500 text-sm">Clique no cliente para expandir e ver:</p>
              <ul className="text-stone-500 text-xs mt-2 ml-4 list-disc">
                <li>Código do painel</li>
                <li>Tamanho</li>
                <li>Descrição do defeito</li>
              </ul>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="bg-stone-100 px-4 py-3 border-b border-stone-200">
              <h4 className="font-semibold text-stone-800">Painéis Descontinuados</h4>
            </div>
            <div className="p-4">
              <p className="text-stone-600 text-sm mb-2">Lista de clientes com painéis descontinuados (fora de catálogo).</p>
              <p className="text-stone-500 text-sm">Mostra o valor total (€) dos painéis descontinuados por cliente.</p>
            </div>
          </div>
        </div>
      </div>
    )
  }
];

export default function UserGuide() {
  const [expandedSection, setExpandedSection] = useState("dashboard");

  const toggleSection = (id) => {
    setExpandedSection(expandedSection === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <PageHeader
            icon={BookOpen}
            title="Guia do Utilizador"
            subtitle="GRESART Showroom Control"
            actions={
              <a 
                href="/api/guide/pdf" 
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-[#009392] text-white rounded-lg hover:bg-[#007b7a] transition-colors"
              >
                <Download size={18} />
                <span className="hidden sm:inline">Descarregar PDF</span>
              </a>
            }
          />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Quick Navigation */}
        <div className="bg-white rounded-xl border border-stone-200 p-4 mb-8">
          <h3 className="font-semibold text-stone-800 mb-3">Navegação rápida:</h3>
          <div className="flex flex-wrap gap-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => {
                  setExpandedSection(section.id);
                  document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                }}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  expandedSection === section.id 
                    ? 'bg-[#009392] text-white' 
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {section.title.split('. ')[1] || section.title}
              </button>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {sections.map((section) => (
            <div 
              key={section.id} 
              id={section.id}
              className="bg-white rounded-xl border border-stone-200 overflow-hidden"
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-stone-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#009392]/10 rounded-lg flex items-center justify-center">
                    <section.icon size={20} className="text-[#009392]" />
                  </div>
                  <h2 className="text-lg font-semibold font-[Manrope] text-stone-900">{section.title}</h2>
                </div>
                {expandedSection === section.id ? (
                  <ChevronDown size={20} className="text-stone-400" />
                ) : (
                  <ChevronRight size={20} className="text-stone-400" />
                )}
              </button>
              {expandedSection === section.id && (
                <div className="px-6 pb-6 pt-2 border-t border-stone-100">
                  {section.content}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-stone-500 text-sm">
          <p>GRESART Showroom Control - Versão 2.0</p>
          <p className="mt-1">Atualizado: Julho 2026</p>
          <p className="mt-1">www.gresart.com</p>
        </div>
      </div>
    </div>
  );
}
